import { inject, Injectable, linkedSignal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AccountSelectionService,
  AsinService,
  SbStrategyService,
  SdStrategyService,
  SpStrategyService,
} from "@front/m19-services";
import { CatalogManagementMode } from "@m19-board/catalog/catalog-sections/catalog.component";
import { combineLatest, filter, map, Observable, switchMap, take, tap } from "rxjs";
import { LoadingService } from "./loading.service";
import { FilterCategory } from "@m19-board/shared/compact-filter/compact-filter.component";
import { AsinFilterService } from "@m19-board/shared/filter/asin-filter.service";
import { ProductEx } from "@front/m19-models";
import { Marketplace, Strategy, StrategyStateEnum } from "@front/m19-api-client";

export interface UnmanagedCatalogProductEx extends ProductEx {
  strategies?: Strategy[];
}

@Injectable({
  providedIn: "root",
})
export class UnmanagedCatalogPageService {
  private readonly asinService = inject(AsinService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly loadingService = inject(LoadingService);
  private readonly accountMarketplace$ = this.accountSelectionService.singleAccountMarketplaceSelection$;
  private readonly asinFilterService = inject(AsinFilterService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly sdStrategyService = inject(SdStrategyService);

  public readonly filters$ = this.asinFilterService.filters$.pipe(
    filter((filter) => filter.length > 0),
    map((filters) =>
      filters
        .filter((filter) => filter.type === FilterCategory.BrandProducts)
        .map((filter) => ({
          ...filter,
          selectAllOption: true,
        })),
    ),
    take(1),
  );

  private readonly _unmanagedCatalog = toSignal(
    this.accountMarketplace$.pipe(
      tap(() => this.loadingService.loadingOn()),
      switchMap((accountMarketplace) =>
        this.getEnhancedUnmanagedCatalog(accountMarketplace.accountId, accountMarketplace.marketplace),
      ),
      tap(() => this.loadingService.loadingOff()),
    ),
  );

  public unmanagedCatalog = linkedSignal({
    source: this._unmanagedCatalog,
    computation: (catalog) => catalog,
    equal: () => false,
  });

  readonly isAutoCatalogManagement = toSignal<boolean>(
    this.accountMarketplace$.pipe(
      switchMap((accountMarketplace) =>
        this.asinService.getCatalogManagementMode(accountMarketplace.accountId, accountMarketplace.marketplace),
      ),
      map((mode) => mode === CatalogManagementMode.AUTO),
    ),
  );

  selectedRows = signal<Set<string>>(new Set<string>());

  public restoreManagingProducts(asins: string[]) {
    return this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      take(1),
      tap(() => this.loadingService.loadingOn()),
      switchMap((accountMarketplace) =>
        this.asinService.addAsins(accountMarketplace.accountId, accountMarketplace.marketplace, asins),
      ),
      tap(() => {
        this.loadingService.loadingOff();
        this.unmanagedCatalog.update((catalog) => {
          return [...catalog!.filter((product) => !asins.includes(product.asin!))];
        });
      }),
    );
  }

  private getEnhancedUnmanagedCatalog(
    accountId: string,
    marketplace: Marketplace,
  ): Observable<UnmanagedCatalogProductEx[]> {
    return this.asinService.getUnmanagedCatalog(accountId, marketplace).pipe(
      map((catalog) => catalog.products),
      switchMap((products) => {
        return combineLatest([
          this.spStrategyService.getStrategiesPerAsin(accountId, marketplace),
          this.sbStrategyService.getStrategiesPerAsin(accountId, marketplace),
          this.sdStrategyService.getStrategiesPerAsin(accountId, marketplace),
        ]).pipe(
          map(([spStrategies, sbStrategies, sdStrategies]) => {
            return products.map((product) =>
              this.expandProduct(
                product,
                spStrategies.get(product.asin!),
                sbStrategies.get(product.asin!),
                sdStrategies.get(product.asin!),
              ),
            );
          }),
        );
      }),
    );
  }

  private expandProduct(
    product: ProductEx,
    spStrategies?: Strategy[],
    sbStrategies?: Strategy[],
    sdStrategies?: Strategy[],
  ): UnmanagedCatalogProductEx {
    return {
      ...product,
      strategies: [
        ...(spStrategies?.filter((s) => s.state === StrategyStateEnum.ENABLED) ?? []),
        ...(sbStrategies?.filter((s) => s.state === StrategyStateEnum.ENABLED) ?? []),
        ...(sdStrategies?.filter((s) => s.state === StrategyStateEnum.ENABLED) ?? []),
      ],
    };
  }
}
